import { BookOpen, Clock, FolderOpen } from "lucide-react";
import { isPermissionEnabled } from "../../utilis/permissionStorage";
import { MODULE_PAGE_SHELL_STYLE } from "../TaskManagement/workspaceConstants";

export const CMS_PRIMARY = "#007BFF";

export const COMPANY_FORMS_PAGE_SHELL = "company-forms-module px-2 pb-8 sm:px-3";
export const COMPANY_FORMS_MODULE_PAGE_SHELL = `${COMPANY_FORMS_PAGE_SHELL} company-forms-module-page-shell box-border min-h-[calc(100vh-7.25rem)]`;
export const COMPANY_FORMS_MODULE_CONTENT = "company-forms-module-content";
/** @deprecated use COMPANY_FORMS_MODULE_* — kept for existing imports */
export const COMPANY_FORMS_APPROVE_PAGE_SHELL = COMPANY_FORMS_MODULE_PAGE_SHELL;
export const COMPANY_FORMS_APPROVE_CONTENT = COMPANY_FORMS_MODULE_CONTENT;
export const COMPANY_FORMS_ROOT = "tailwind-container company-forms-root";

export { MODULE_PAGE_SHELL_STYLE };

export const DEFAULT_COMPANY_FORM_CATEGORIES = [
  { value: "terms_conditions", label: "Terms & Conditions" },
  { value: "service_agreement", label: "Service Agreement" },
  { value: "other", label: "Other" },
];

/** @deprecated use API categories; kept for fallback */
export const COMPANY_FORM_CATEGORIES = DEFAULT_COMPANY_FORM_CATEGORIES;

export function buildCategoryLabels(categories) {
  return Object.fromEntries(
    (categories || []).map((c) => [c.value, c.label]),
  );
}

export const CATEGORY_LABELS = buildCategoryLabels(DEFAULT_COMPANY_FORM_CATEGORIES);

export function resolveCategoryLabel(slug, labels = CATEGORY_LABELS) {
  if (!slug) return "—";
  return labels[slug] || slug.replace(/_/g, " ");
}

/** Matches `cms-backend/config/company_forms.php` destination_countries. */
export const COMPANY_FORM_GLOBAL_DESTINATION = "Global";

export const DEFAULT_COMPANY_FORM_DESTINATIONS = [
  COMPANY_FORM_GLOBAL_DESTINATION,
  "Australia",
  "Canada",
  "New Zealand",
  "UK",
  "USA",
  "Pakistan",
  "Germany",
  "Ireland",
  "France",
  "UAE",
  "Other",
];

export function resolveDestinationCountry(value) {
  const trimmed = String(value || "").trim();
  return trimmed || COMPANY_FORM_GLOBAL_DESTINATION;
}

export function compareCompanyFormListing(a, b) {
  const global = COMPANY_FORM_GLOBAL_DESTINATION;
  const destA = resolveDestinationCountry(a?.destination_country);
  const destB = resolveDestinationCountry(b?.destination_country);
  const globalRankA = destA === global ? 0 : 1;
  const globalRankB = destB === global ? 0 : 1;
  if (globalRankA !== globalRankB) return globalRankA - globalRankB;
  if (destA !== destB) return destA.localeCompare(destB);
  const catA = a?.category || "";
  const catB = b?.category || "";
  if (catA !== catB) return catA.localeCompare(catB);
  return String(a?.name || "").localeCompare(String(b?.name || ""));
}

export function groupLibraryFormsByDestination(forms) {
  const groups = [];
  let current = null;
  for (const form of forms || []) {
    const key = resolveDestinationCountry(form.destination_country);
    if (!current || current.key !== key) {
      current = { key, forms: [] };
      groups.push(current);
    }
    current.forms.push(form);
  }
  return groups;
}

export const ACCEPTED_FORM_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const ACCEPTED_FORM_EXTENSIONS = [".pdf", ".doc", ".docx"];
export const MAX_FORM_BYTES = 25 * 1024 * 1024;

export const LIBRARY_STAT_CARDS = [
  {
    key: "total",
    label: "Published documents",
    sub: "Approved & current",
    Icon: BookOpen,
    color: CMS_PRIMARY,
    iconBg: "#EFF6FF",
    iconBorder: "#BFDBFE",
  },
  {
    key: "categories",
    label: "Categories",
    sub: "Available filters",
    Icon: FolderOpen,
    color: "#2563EB",
    iconBg: "#EFF6FF",
    iconBorder: "#BFDBFE",
  },
];

export const MANAGE_STAT_CARDS = [
  {
    key: "total",
    label: "All documents",
    sub: "Registered in CMS",
    Icon: BookOpen,
    color: CMS_PRIMARY,
    iconBg: "#EFF6FF",
    iconBorder: "#BFDBFE",
  },
  {
    key: "pending",
    label: "Awaiting approval",
    sub: "Pending review",
    Icon: Clock,
    color: "#D97706",
    iconBg: "#FFFBEB",
    iconBorder: "#FDE68A",
  },
];

export function formatFormDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatVersionStatusLabel(status) {
  if (!status) return "—";
  switch (status) {
    case "pending_approval":
      return "Pending approval";
    case "published":
      return "Published";
    case "superseded":
      return "Superseded";
    case "rejected":
      return "Rejected";
    default:
      return String(status).replace(/_/g, " ");
  }
}

/** Compact label for table status cells */
export function formatVersionStatusShort(status, { isCurrent = false } = {}) {
  if (!status) return "—";
  switch (status) {
    case "pending_approval":
      return "Pending";
    case "published":
      return isCurrent ? "Live" : "Published";
    case "superseded":
      return "Superseded";
    case "rejected":
      return "Rejected";
    default:
      return String(status).replace(/_/g, " ");
  }
}

function versionActivityTimestamp(version) {
  const stamps = [
    version?.created_at,
    version?.approved_at,
    version?.rejected_at,
  ].filter(Boolean);
  if (!stamps.length) return 0;
  return Math.max(...stamps.map((value) => new Date(value).getTime()));
}

/** Flat rows for manage table: newest activity first (fallback when API returns legacy form payloads). */
export function buildManageTableRows(forms) {
  const rows = [];
  for (const form of forms || []) {
    for (const version of form.versions || []) {
      rows.push({ form, version });
    }
  }
  rows.sort((a, b) => {
    const activityDiff =
      versionActivityTimestamp(b.version) - versionActivityTimestamp(a.version);
    if (activityDiff !== 0) return activityDiff;
    const formUpdatedDiff =
      new Date(b.form?.updated_at || 0).getTime() -
      new Date(a.form?.updated_at || 0).getTime();
    if (formUpdatedDiff !== 0) return formUpdatedDiff;
    return (b.version?.version_number || 0) - (a.version?.version_number || 0);
  });
  return rows;
}

export function normalizeManageIndexRows(payload) {
  const data = payload?.data;
  if (!Array.isArray(data) || data.length === 0) {
    return { rows: [], documentCount: payload?.document_count ?? 0 };
  }
  const first = data[0];
  if (first && first.form && first.version) {
    return {
      rows: data,
      documentCount: payload?.document_count ?? 0,
    };
  }
  return {
    rows: buildManageTableRows(data),
    documentCount: payload?.document_count ?? data.length,
  };
}

export function formHasPendingVersion(form) {
  return (form?.versions || []).some((v) => v.status === "pending_approval");
}

export function formHasPublishedVersion(form) {
  return (form?.versions || []).some((v) => v.status === "published");
}

export function canEditCompanyForm(form, permissions, version = null) {
  return canManageCompanyDocuments(permissions);
}

/** Upload or approve — bulk delete on manage table. */
export function canBulkDeleteCompanyForms(permissions) {
  return canManageCompanyDocuments(permissions);
}

/** Upload or approve — edit/delete any document type including live/published. */
export function canManageCompanyDocuments(permissions) {
  const p = normalizeCompanyFormPermissions(permissions);
  return Boolean(p.upload_company_forms || p.approve_company_forms);
}

/** @deprecated use canManageCompanyDocuments */
export function canDeleteAllCompanyDocumentTypes(permissions) {
  return canManageCompanyDocuments(permissions);
}

export function canUploadCompanyForms(permissions) {
  const p = normalizeCompanyFormPermissions(permissions);
  return Boolean(p.upload_company_forms || p.approve_company_forms);
}

export function canApproveCompanyForms(permissions) {
  return normalizeCompanyFormPermissions(permissions).approve_company_forms;
}

/** Upload or approve may manage category labels. */
export function canManageCompanyFormCategories(permissions) {
  return canManageCompanyDocuments(permissions);
}

function normalizeCompanyFormPermissions(permissions) {
  if (!permissions || typeof permissions !== "object") {
    return {
      upload_company_forms: false,
      approve_company_forms: false,
    };
  }
  if ("canUpload" in permissions || "canApprove" in permissions) {
    return {
      upload_company_forms: Boolean(permissions.canUpload),
      approve_company_forms: Boolean(permissions.canApprove),
    };
  }
  return {
    upload_company_forms: isPermissionEnabled(permissions.upload_company_forms),
    approve_company_forms: isPermissionEnabled(permissions.approve_company_forms),
  };
}

export function canDeleteCompanyForm(form, permissions) {
  return canManageCompanyDocuments(permissions);
}

export function canDeleteCompanyFormVersion(version, permissions) {
  return canManageCompanyDocuments(permissions);
}

export function openFormFile(version) {
  const url = version?.file_url;
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

export function destinationBadgeClass(destination) {
  if (resolveDestinationCountry(destination) === COMPANY_FORM_GLOBAL_DESTINATION) {
    return "company-forms-destination-badge--global";
  }
  return "company-forms-destination-badge--country";
}

export function categoryBadgeClass(category) {
  switch (category) {
    case "terms_conditions":
      return "company-forms-category-badge--terms";
    case "service_agreement":
      return "company-forms-category-badge--service";
    default:
      return "company-forms-category-badge--other";
  }
}

export function statusBadgeClass(status) {
  switch (status) {
    case "published":
      return "company-forms-status-pill--published";
    case "pending_approval":
      return "company-forms-status-pill--pending";
    case "superseded":
      return "company-forms-status-pill--superseded";
    case "rejected":
      return "company-forms-status-pill--rejected";
    default:
      return "company-forms-status-pill--default";
  }
}
